import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2, Sparkles, Search, Gauge, Smartphone, TrendingUp, Cpu, ShieldCheck, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * HomepageAuditBlock — compact conversion-focused section directly below
 * the homepage hero. Reuses the existing /audit engine (POST /api/audits)
 * and hands off to the existing processing/report pages. Never duplicates
 * audit logic.
 */

const BENEFITS = [
  { icon: Search,      label: "SEO & Google visibility" },
  { icon: Gauge,       label: "Website performance" },
  { icon: Smartphone,  label: "Mobile experience" },
  { icon: TrendingUp,  label: "Lead generation" },
  { icon: Cpu,         label: "AI Search readiness" },
];

// Sample values shown ONLY on the right-hand mockup, clearly labelled.
const SAMPLE = {
  score: 72,
  categories: [
    { k: "Conversion", v: 58, color: "#F59E0B" },
    { k: "Local SEO",  v: 64, color: "#FF6A00" },
    { k: "Technical",  v: 81, color: "#22C55E" },
    { k: "Mobile",     v: 92, color: "#22C55E" },
  ],
  opportunity: "Strengthen conversion path — clear CTAs, phone in header, quote form above the fold.",
};

export default function HomepageAuditBlock() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!url.trim()) { toast.error("Enter your website URL"); return; }
    setLoading(true);
    try {
      const r = await api.post("/audits", { website_url: url.trim() });
      const pid = r?.data?.public_id;
      if (!pid) throw new Error("no id");
      if (r?.data?.cached) navigate(`/audit/report/${pid}`);
      else navigate(`/audit/processing/${pid}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "We couldn't start the audit. Please check the URL and try again.";
      toast.error(typeof msg === "string" ? msg : "Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <section
      data-testid="section-homepage-audit"
      className="relative bg-[#050505] text-white border-t border-white/5 overflow-hidden"
    >
      <div className="absolute -top-24 -left-24 w-[500px] h-[500px] rounded-full bg-[#FF6A00]/12 blur-[160px] pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-[500px] h-[500px] rounded-full bg-[#0066FF]/10 blur-[160px] pointer-events-none" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center relative">
        {/* LEFT — copy + form */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-white/70 border border-white/10 rounded-full px-3 py-1.5"
            data-testid="homepage-audit-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF6A00] animate-pulse" />
            Free Website Growth Audit
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-4 font-display font-bold tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[1.02] max-w-xl"
            data-testid="homepage-audit-headline"
          >
            See What&apos;s <span className="text-gradient-bo">Holding Your Website Back.</span>
          </motion.h2>

          <p className="mt-4 text-white/70 leading-relaxed max-w-xl" data-testid="homepage-audit-sub">
            In under a minute our AI-powered audit reveals the biggest SEO, performance, mobile, conversion, local SEO and AI-search opportunities on your site — with clear next steps.
          </p>

          <form
            onSubmit={submit}
            noValidate
            className="mt-6 max-w-xl rounded-2xl border border-white/10 bg-[#0A0A0A] p-2 flex flex-col sm:flex-row gap-2 shadow-[0_20px_60px_-20px_rgba(255,106,0,0.35)]"
            data-testid="homepage-audit-form"
          >
            <input
              type="url"
              inputMode="url"
              autoComplete="url"
              placeholder="https://yourwebsite.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              data-testid="homepage-audit-url-input"
              aria-label="Website URL"
              className="flex-1 min-w-0 bg-transparent px-4 py-3 text-sm sm:text-base text-white placeholder:text-white/30 focus:outline-none"
              required
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="homepage-audit-submit"
              className="btn-primary justify-center whitespace-nowrap disabled:opacity-70"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? "Starting…" : "Analyze My Website"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          <p className="mt-3 text-xs text-white/40 inline-flex items-center gap-2">
            <ShieldCheck className="w-3 h-3" /> Free report · No credit card · No obligation
          </p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3 max-w-xl" data-testid="homepage-audit-benefits">
            {BENEFITS.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2 text-sm text-white/85">
                <span className="w-6 h-6 rounded-md bg-[#FF6A00]/15 text-[#FF6A00] grid place-items-center shrink-0">
                  <Icon className="w-3.5 h-3.5" />
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>

        {/* RIGHT — sample audit mockup (clearly labelled) */}
        <div className="lg:col-span-5 relative" data-testid="homepage-audit-sample">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-[#0A0A0A] to-[#141414] p-6 sm:p-7 shadow-[0_40px_120px_-40px_rgba(255,106,0,0.4)]"
          >
            <div className="absolute -top-3 left-6 text-[10px] font-mono uppercase tracking-[0.2em] bg-[#FF6A00] text-black px-2 py-1 rounded-md">
              Sample Report
            </div>
            <div className="flex items-center justify-between gap-3 mt-2">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Overall Score</div>
                <div className="mt-1 font-display font-bold text-5xl leading-none text-[#FF6A00]">
                  {SAMPLE.score}
                  <span className="text-lg text-white/50 font-normal ml-1">/100</span>
                </div>
              </div>
              <ScoreDial value={SAMPLE.score} />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-2">
              {SAMPLE.categories.map((c) => (
                <div key={c.k} className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-white/75">{c.k}</span>
                    <span className="font-mono font-bold text-xs" style={{ color: c.color }}>{c.v}</span>
                  </div>
                  <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${c.v}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.9, ease: "easeOut" }}
                      className="h-full rounded-full"
                      style={{ background: c.color }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-xl border border-[#FF6A00]/25 bg-[#FF6A00]/5 p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF6A00] mb-1">Biggest opportunity</div>
              <div className="text-sm text-white/85 leading-relaxed flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#22C55E] mt-0.5 shrink-0" />
                <span>{SAMPLE.opportunity}</span>
              </div>
            </div>

            <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-white/35 text-center">
              Sample only · Your real report is generated live
            </div>
          </motion.div>

          {/* Small decorative badge */}
          <div className="absolute -bottom-4 -left-4 hidden lg:block rounded-2xl border border-white/10 bg-[#0A0A0A] px-4 py-3 shadow-xl">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Analysed in</div>
            <div className="font-display font-bold text-lg text-white">&lt; 45 seconds</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ScoreDial({ value }) {
  const r = 32;
  const c = 40;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.max(0, Math.min(100, value)) / 100) * circ;
  return (
    <div className="relative w-20 h-20 shrink-0">
      <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
        <circle cx={c} cy={c} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
        <motion.circle
          cx={c} cy={c} r={r}
          stroke="#FF6A00" strokeWidth="6" fill="none" strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          whileInView={{ strokeDashoffset: offset }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <Sparkles className="w-5 h-5 text-[#FF6A00]" />
      </div>
    </div>
  );
}
